import { collection, doc, getDoc, setDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';

// واجهة لبيانات استخدام فايربيز
export interface FirebaseUsageData {
  date: Date;
  storage: {
    total: number;
    used: number;
    files: number;
  };
  database: {
    reads: number;
    writes: number;
    deletes: number;
    collections: number;
    documents: number;
  };
  auth: {
    users: number;
    logins: number;
  };
  functions: {
    invocations: number;
    errors: number;
  };
  hosting: {
    requests: number;
    bandwidth: number;
  };
  cloudinary?: {
    storage: number;
    bandwidth: number;
    transformations: number;
    images: number;
    videos: number;
  };
}

// واجهة لإحصائيات استخدام فايربيز
export interface FirebaseUsageStats {
  dailyUsage: FirebaseUsageData[];
  monthlyUsage: FirebaseUsageData[];
  currentUsage: FirebaseUsageData;
  limits: {
    storage: number;
    databaseReads: number;
    databaseWrites: number;
    authUsers: number;
    functionInvocations: number;
  };
}

// الحصول على بيانات استخدام فايربيز
export const getFirebaseUsage = async (): Promise<FirebaseUsageStats> => {
  try {
    // محاولة الحصول على بيانات الاستخدام من Firestore
    const usageDoc = await getDoc(doc(db, 'system', 'firebaseUsage'));

    // إذا كانت البيانات موجودة، قم بإرجاعها
    if (usageDoc.exists()) {
      const data = usageDoc.data() as FirebaseUsageStats;

      // تحويل الطوابع الزمنية إلى كائنات Date
      const processDate = (item: any) => {
        if (item.date && typeof item.date.toDate === 'function') {
          item.date = item.date.toDate();
        } else if (item.date && item.date.seconds) {
          item.date = new Date(item.date.seconds * 1000);
        }
        return item;
      };

      return {
        dailyUsage: data.dailyUsage.map(processDate),
        monthlyUsage: data.monthlyUsage.map(processDate),
        currentUsage: processDate(data.currentUsage),
        limits: data.limits
      };
    }

    // إذا لم تكن البيانات موجودة، قم بإنشاء بيانات افتراضية
    return generateMockUsageData();
  } catch (error) {
    console.error('Error getting Firebase usage data:', error);
    // في حالة حدوث خطأ، قم بإرجاع بيانات افتراضية
    return generateMockUsageData();
  }
};

// إنشاء بيانات استخدام افتراضية للعرض
const generateMockUsageData = (): FirebaseUsageStats => {
  const today = new Date();
  const dailyUsage: FirebaseUsageData[] = [];
  const monthlyUsage: FirebaseUsageData[] = [];

  // إنشاء بيانات يومية للأيام الـ 30 الماضية
  for (let i = 29; i >= 0; i--) {
    const date = new Date();
    date.setDate(today.getDate() - i);

    dailyUsage.push({
      date,
      storage: {
        total: 5 * 1024 * 1024 * 1024, // 5GB
        used: Math.floor(Math.random() * 3 * 1024 * 1024 * 1024), // 0-3GB
        files: Math.floor(Math.random() * 1000) + 500
      },
      database: {
        reads: Math.floor(Math.random() * 10000) + 1000,
        writes: Math.floor(Math.random() * 2000) + 500,
        deletes: Math.floor(Math.random() * 500) + 100,
        collections: 15,
        documents: Math.floor(Math.random() * 1000) + 5000
      },
      auth: {
        users: 100 + Math.floor(Math.random() * 10),
        logins: Math.floor(Math.random() * 200) + 50
      },
      functions: {
        invocations: Math.floor(Math.random() * 1000) + 200,
        errors: Math.floor(Math.random() * 50)
      },
      hosting: {
        requests: Math.floor(Math.random() * 5000) + 1000,
        bandwidth: Math.floor(Math.random() * 1024 * 1024 * 500) // 0-500MB
      },
      cloudinary: {
        storage: Math.floor(Math.random() * 2 * 1024 * 1024 * 1024), // 0-2GB
        bandwidth: Math.floor(Math.random() * 5 * 1024 * 1024 * 1024), // 0-5GB
        transformations: Math.floor(Math.random() * 1000) + 200,
        images: Math.floor(Math.random() * 500) + 100,
        videos: Math.floor(Math.random() * 50) + 10
      }
    });
  }

  // إنشاء بيانات شهرية للأشهر الـ 12 الماضية
  for (let i = 11; i >= 0; i--) {
    const date = new Date();
    date.setMonth(today.getMonth() - i);
    date.setDate(1);

    monthlyUsage.push({
      date,
      storage: {
        total: 5 * 1024 * 1024 * 1024, // 5GB
        used: Math.floor(Math.random() * 4 * 1024 * 1024 * 1024), // 0-4GB
        files: Math.floor(Math.random() * 2000) + 1000
      },
      database: {
        reads: Math.floor(Math.random() * 100000) + 10000,
        writes: Math.floor(Math.random() * 20000) + 5000,
        deletes: Math.floor(Math.random() * 5000) + 1000,
        collections: 15,
        documents: Math.floor(Math.random() * 2000) + 5000
      },
      auth: {
        users: 100 + Math.floor(Math.random() * 20),
        logins: Math.floor(Math.random() * 2000) + 500
      },
      functions: {
        invocations: Math.floor(Math.random() * 10000) + 2000,
        errors: Math.floor(Math.random() * 500)
      },
      hosting: {
        requests: Math.floor(Math.random() * 50000) + 10000,
        bandwidth: Math.floor(Math.random() * 1024 * 1024 * 5000) // 0-5GB
      },
      cloudinary: {
        storage: Math.floor(Math.random() * 3 * 1024 * 1024 * 1024), // 0-3GB
        bandwidth: Math.floor(Math.random() * 10 * 1024 * 1024 * 1024), // 0-10GB
        transformations: Math.floor(Math.random() * 5000) + 1000,
        images: Math.floor(Math.random() * 1000) + 500,
        videos: Math.floor(Math.random() * 100) + 20
      }
    });
  }

  // استخدام آخر يوم كبيانات حالية
  const currentUsage = { ...dailyUsage[dailyUsage.length - 1] };

  return {
    dailyUsage,
    monthlyUsage,
    currentUsage,
    limits: {
      storage: 5 * 1024 * 1024 * 1024, // 5GB
      databaseReads: 50000,
      databaseWrites: 20000,
      authUsers: 100,
      functionInvocations: 125000
    }
  };
};

// حفظ بيانات استخدام فايربيز
export const saveFirebaseUsage = async (usageData: FirebaseUsageStats): Promise<void> => {
  try {
    await setDoc(doc(db, 'system', 'firebaseUsage'), usageData);
    console.log('Firebase usage data saved successfully');
  } catch (error) {
    console.error('Error saving Firebase usage data:', error);
    throw error;
  }
};

// تحديث بيانات استخدام فايربيز الحالية
export const updateCurrentUsage = async (usageData: Partial<FirebaseUsageData>): Promise<void> => {
  try {
    const currentData = await getFirebaseUsage();
    const updatedCurrentUsage = {
      ...currentData.currentUsage,
      ...usageData,
      date: new Date()
    };

    await setDoc(doc(db, 'system', 'firebaseUsage'), {
      ...currentData,
      currentUsage: updatedCurrentUsage
    });

    console.log('Current Firebase usage updated successfully');
  } catch (error) {
    console.error('Error updating current Firebase usage:', error);
    throw error;
  }
};

export default {
  getFirebaseUsage,
  saveFirebaseUsage,
  updateCurrentUsage
};
